import { useCallback, useEffect, useRef, useState } from 'react';

export type PropertySectionKey =
    | 'basicInfo'
    | 'propertyFacts'
    | 'contacts'
    | 'tourSettings'
    | 'applicationLinks';

/**
 * Anchor id for the tour-availability subsection, which lives inside Tour
 * Settings without a nav entry of its own and so is scrolled to by id rather
 * than through the section nav.
 */
export const PROPERTY_TOUR_AVAILABILITY_ANCHOR_ID = 'property-tour-availability-section';
export const PROPERTY_INTEGRATIONS_ANCHOR_ID = 'property-integrations-section';

const SCROLL_SPY_OFFSET = 80;
const SCROLL_SPY_SUPPRESS_MS = 800;

export function usePropertySectionNavigation(
    visibleSections: PropertySectionKey[],
    initialSection: PropertySectionKey = 'basicInfo',
) {
    const contentRef = useRef<HTMLDivElement>(null);
    const sectionRefs = useRef<Partial<Record<PropertySectionKey, HTMLElement | null>>>({});
    const spySuppressTimeoutRef = useRef<number | null>(null);
    const [activeSection, setActiveSection] = useState<PropertySectionKey>(initialSection);

    const setSectionRef = useCallback(
        (key: PropertySectionKey) => (element: HTMLElement | null) => {
            sectionRefs.current[key] = element;
        },
        []
    );

    useEffect(() => () => {
        if (spySuppressTimeoutRef.current != null) {
            window.clearTimeout(spySuppressTimeoutRef.current);
        }
    }, []);

    const suppressSpy = useCallback(() => {
        if (spySuppressTimeoutRef.current != null) {
            window.clearTimeout(spySuppressTimeoutRef.current);
        }
        spySuppressTimeoutRef.current = window.setTimeout(() => {
            spySuppressTimeoutRef.current = null;
        }, SCROLL_SPY_SUPPRESS_MS);
    }, []);

    const scrollToSection = useCallback((key: PropertySectionKey) => {
        setActiveSection(key);
        suppressSpy();

        const container = contentRef.current;
        const section = sectionRefs.current[key];
        if (container && section) {
            const titleElement = section.querySelector<HTMLElement>('[data-property-section-title]') ?? section;
            const containerTop = container.getBoundingClientRect().top;
            const titleTop = titleElement.getBoundingClientRect().top;
            container.scrollTo({
                top: container.scrollTop + titleTop - containerTop - 8,
                behavior: 'smooth',
            });
        }
    }, [suppressSpy]);

    const scrollToAnchor = useCallback((anchorId: string) => {
        suppressSpy();
        contentRef.current
            ?.querySelector(`#${anchorId}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [suppressSpy]);

    const handleContentScroll = useCallback(() => {
        const container = contentRef.current;
        if (!container || spySuppressTimeoutRef.current != null) {
            return;
        }

        let current = visibleSections[0];
        const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 4;
        if (isAtBottom) {
            current = visibleSections[visibleSections.length - 1];
        } else {
            const containerTop = container.getBoundingClientRect().top;
            for (const key of visibleSections) {
                const element = sectionRefs.current[key];
                if (element && element.getBoundingClientRect().top - containerTop <= SCROLL_SPY_OFFSET) {
                    current = key;
                }
            }
        }

        setActiveSection(current);
    }, [visibleSections]);

    return {
        activeSection,
        contentRef,
        handleContentScroll,
        scrollToSection,
        scrollToAnchor,
        setSectionRef,
    };
}
